import React, { createContext, useContext, useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { setCurrentSelectedRole } from "@/services/api/apiUtils";

type UserRole =
  | "buyer"
  | "admin"
  | "document_manager"
  | "finance_manager"
  | "support";

// Permission definition type
interface Permission {
  id: number;
  action: string;
  entity_name: string;
}

// Role definition type
interface Role {
  id: number;
  name: string;
  companyName?: string; // For management company roles
  associationName?: string; // For association roles
}

// Role permissions map type
interface RolePermissionsMap {
  [roleId: number]: Permission[];
}

// Selected role interface
interface SelectedRole {
  type: "platform" | "management_company" | "association";
  roleId?: number;
  roleName?: string;
  entityId?: number;
  entityName?: string;
}

// User roles structure type
interface UserRoles {
  platform: Role[];
  managementCompanies: {
    [managementCompanyId: number]: Role[];
  };
  associations: {
    [associationId: number]: Role[];
  };
}

// Empty roles structure
const emptyRoles: UserRoles = {
  platform: [],
  managementCompanies: {},
  associations: {},
};

interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: UserRole;
  management_company_id?: number;
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  permissionsLoaded: boolean;
  userRoles: UserRoles;
  rolePermissions: RolePermissionsMap;
  selectedRole: SelectedRole | null;
  setSelectedRole: (role: SelectedRole | null) => void;
  fetchUserRoles: (userId: number) => Promise<UserRoles>;
  hasPermission: (
    roleIds: number[],
    action: string,
    entityName: string
  ) => boolean;
  getCurrentRoleIds: () => number[];
  login: (
    email: string,
    password: string,
    managementCompanyId?: number
  ) => Promise<void>;
  register: (
    email: string,
    password: string,
    firstName: string,
    lastName: string
  ) => Promise<void>;
  logout: () => Promise<void>;
  isAuthenticated: boolean;
  currentUser: User | null; // Add this line to expose currentUser as well
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [permissionsLoaded, setPermissionsLoaded] = useState(false);
  const [userRoles, setUserRoles] = useState<UserRoles>(emptyRoles);
  const [rolePermissions, setRolePermissions] = useState<RolePermissionsMap>(
    {}
  );
  const [selectedRole, setSelectedRole] = useState<SelectedRole | null>(null);
  const [rolesLoaded, setRolesLoaded] = useState(false);

  // Helper function to load selected role from localStorage
  const loadSelectedRoleFromStorage = (): SelectedRole | null => {
    try {
      const storedRole = localStorage.getItem("selectedRole");
      return storedRole ? JSON.parse(storedRole) : null;
    } catch (error) {
      console.error("Error loading selected role from localStorage:", error);
      return null;
    }
  };

  // Helper function to save selected role to localStorage
  const saveSelectedRoleToStorage = (role: SelectedRole | null) => {
    try {
      if (role) {
        localStorage.setItem("selectedRole", JSON.stringify(role));
      } else {
        localStorage.removeItem("selectedRole");
      }
    } catch (error) {
      console.error("Error saving selected role to localStorage:", error);
    }
  };

  // Helper function to validate if a stored role is still valid for current user
  const isStoredRoleValid = (
    storedRole: SelectedRole,
    currentUserRoles: UserRoles
  ): boolean => {
    switch (storedRole.type) {
      case "platform":
        return currentUserRoles.platform.length > 0;
      case "management_company":
        return storedRole.entityId
          ? !!currentUserRoles.managementCompanies[storedRole.entityId]
          : false;
      case "association":
        return storedRole.entityId
          ? !!currentUserRoles.associations[storedRole.entityId]
          : false;
      default:
        return false;
    }
  };

  // Check if we should set loading to false - only when both permissions and roles are loaded
  useEffect(() => {
    if (permissionsLoaded && rolesLoaded) {
      setLoading(false);
    }
  }, [permissionsLoaded, rolesLoaded]);

  // Function to get role IDs for the currently selected role
  const getCurrentRoleIds = (): number[] => {
    if (!selectedRole || !userRoles) return [];

    switch (selectedRole.type) {
      case "platform":
        return userRoles.platform.map((role) => role.id);
      case "management_company":
        if (
          selectedRole.entityId &&
          userRoles.managementCompanies[selectedRole.entityId]
        ) {
          return userRoles.managementCompanies[selectedRole.entityId].map(
            (role) => role.id
          );
        }
        return [];
      case "association":
        if (
          selectedRole.entityId &&
          userRoles.associations[selectedRole.entityId]
        ) {
          return userRoles.associations[selectedRole.entityId].map(
            (role) => role.id
          );
        }
        return [];
      default:
        return [];
    }
  };

  // Auto-select role when userRoles change, with localStorage persistence
  useEffect(() => {
    if (userRoles && !selectedRole) {
      // First, try to load from localStorage
      const storedRole = loadSelectedRoleFromStorage();

      if (storedRole && isStoredRoleValid(storedRole, userRoles)) {
        // If stored role is valid, use it
        setSelectedRole(storedRole);
        return;
      }

      // If no valid stored role, fall back to default selection logic
      // Priority: platform role > management company role > association role
      if (userRoles.platform.length > 0) {
        setSelectedRole({
          type: "platform",
          entityName: "Platform",
        });
      } else {
        const mcEntries = Object.entries(userRoles.managementCompanies);
        if (mcEntries.length > 0 && mcEntries[0][1].length > 0) {
          const [mcId, roles] = mcEntries[0];
          setSelectedRole({
            type: "management_company",
            entityId: parseInt(mcId),
            entityName: roles[0].companyName || `Company ${mcId}`,
          });
        } else {
          const assocEntries = Object.entries(userRoles.associations);
          if (assocEntries.length > 0 && assocEntries[0][1].length > 0) {
            const [assocId, roles] = assocEntries[0];
            setSelectedRole({
              type: "association",
              entityId: parseInt(assocId),
              entityName: roles[0].associationName || `Association ${assocId}`,
            });
          }
        }
      }
    }
  }, [userRoles, selectedRole]);

  // Save selected role to localStorage whenever it changes
  useEffect(() => {
    if (selectedRole) {
      saveSelectedRoleToStorage(selectedRole);
    }
  }, [selectedRole]);

  // Function to fetch all role permissions
  const fetchRolePermissions = async () => {
    try {
      setPermissionsLoaded(false);
      const { data: rolePerms, error } = await supabase.from("role_permissions")
        .select(`
          role_id,
          permissions (
            id,
            action,
            entity_name
          )
        `);

      if (error) throw error;

      const permissionsMap: RolePermissionsMap = {};

      rolePerms?.forEach((rp) => {
        if (!permissionsMap[rp.role_id]) {
          permissionsMap[rp.role_id] = [];
        }
        if (rp.permissions) {
          permissionsMap[rp.role_id].push({
            id: rp.permissions.id,
            action: rp.permissions.action,
            entity_name: rp.permissions.entity_name,
          });
        }
      });

      console.log("permissionsMap", permissionsMap);
      console.log("Sample permissions for role 1:", JSON.stringify(permissionsMap[1]?.slice(0, 5), null, 2));

      setRolePermissions(permissionsMap);
      setPermissionsLoaded(true);
      return permissionsMap;
    } catch (error) {
      console.error("Error fetching role permissions:", error);
      setPermissionsLoaded(true); // Set to true even on error to prevent infinite loading
      return {};
    }
  };

  // Helper function to check if a user has a specific permission
  const hasPermission = (
    roleIds: number[],
    action: string,
    entityName: string
  ): boolean => {
    const result = roleIds.some((roleId) => {
      const permissions = rolePermissions[roleId] || [];
      const hasIt = permissions.some(
        (perm) => perm.action === action && perm.entity_name === entityName
      );
      if (entityName === 'users') {
        const uniqueEntities = [...new Set(permissions.map(p => p.entity_name))];
        console.log('hasPermission check for users:', {
          roleId,
          permissionsCount: permissions.length,
          uniqueEntities: uniqueEntities.join(', '),
          firstFewPermissions: JSON.stringify(permissions.slice(0, 3)),
          lookingFor: { action, entityName },
          hasIt
        });
      }
      return hasIt;
    });
    if (entityName === 'users') {
      console.log('hasPermission final result for users:', { roleIds, action, entityName, result });
    }
    return result;
  };

  // Function to fetch all roles for a user
  const fetchUserRoles = async (userId: number): Promise<UserRoles> => {
    try {
      setRolesLoaded(false);
      // Initialize roles structure
      const roles: UserRoles = {
        platform: [],
        managementCompanies: {},
        associations: {},
      };

      // Fetch all role types in PARALLEL for better performance
      const [platformResult, mcResult, assocResult] = await Promise.all([
        // Fetch platform roles
        supabase
          .from("platform_role_assignments")
          .select("role_id, roles(id, name)")
          .eq("user_id", userId),
        // Fetch management company roles with company names
        supabase
          .from("management_company_role_assignments")
          .select(
            `
            management_company_id,
            role_id,
            roles(id, name),
            management_companies(id, name)
          `
          )
          .eq("user_id", userId),
        // Fetch association roles with association names
        supabase
          .from("association_role_assignments")
          .select(
            `
            association_id,
            role_id,
            roles(id, name),
            associations(id, name)
          `
          )
          .eq("user_id", userId),
      ]);

      // Process platform roles
      if (platformResult.error) throw platformResult.error;
      if (platformResult.data) {
        roles.platform = platformResult.data.map((pr) => ({
          id: pr.roles.id,
          name: pr.roles.name,
        }));
      }

      // Process management company roles
      if (mcResult.error) throw mcResult.error;
      if (mcResult.data) {
        // Group by management company ID and include company names
        mcResult.data.forEach((mcr) => {
          const companyId = mcr.management_company_id;
          if (!roles.managementCompanies[companyId]) {
            roles.managementCompanies[companyId] = [];
          }
          roles.managementCompanies[companyId].push({
            id: mcr.roles.id,
            name: mcr.roles.name,
            companyName: mcr.management_companies.name, // Add company name
          });
        });
      }

      // Process association roles
      if (assocResult.error) throw assocResult.error;
      if (assocResult.data) {
        // Group by association ID and include association names
        assocResult.data.forEach((ar) => {
          const assocId = ar.association_id;
          if (!roles.associations[assocId]) {
            roles.associations[assocId] = [];
          }
          roles.associations[assocId].push({
            id: ar.roles.id,
            name: ar.roles.name,
            associationName: ar.associations.name, // Add association name
          });
        });
      }

      setUserRoles(roles);
      setRolesLoaded(true);

      return roles;
    } catch (error) {
      console.error("Error fetching user roles:", error);
      setRolesLoaded(true); // Set to true even on error to prevent infinite loading
      return emptyRoles;
    }
  };

  console.log("userRoles", userRoles);

  // Function to get database user ID from auth user ID
  const getUserDatabaseId = async (
    authUserId: string
  ): Promise<number | null> => {
    try {
      const { data, error } = await supabase
        .from("users")
        .select("id")
        .eq("auth_user_id", authUserId)
        .single();

      if (error) throw error;
      return data?.id || null;
    } catch (error) {
      console.error("Error fetching user database ID:", error);
      return null;
    }
  };

  useEffect(() => {
    // Set up auth state listener
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, session) => {
      console.log("Auth event:", event);
      console.log("session inside supabase auth state change", session);
      if (session && session.user) {
        const userData = {
          id: session.user.id,
          email: session.user.email || "",
          firstName: session.user.user_metadata?.first_name || "",
          lastName: session.user.user_metadata?.last_name || "",
          role: (session.user.user_metadata?.role as UserRole) || "buyer",
          management_company_id:
            session.user.user_metadata?.management_company_id || undefined,
        };
        setUser(userData);
        localStorage.setItem("user", JSON.stringify(userData));

        // Fetch roles if we have a database user ID
        // const dbUserId = await getUserDatabaseId(session.user.id);
        // if (dbUserId) {
        //   await fetchUserRoles(dbUserId);
        // }
      } else {
        setUser(null);
        setUserRoles(emptyRoles);
        localStorage.removeItem("user");
      }
      // setLoading(false);
    });

    // Check for existing session
    const checkUser = async () => {
      const {
        data: { session },
      } = await supabase.auth.getSession();

      if (session && session.user) {
        const userData = {
          id: session.user.id,
          email: session.user.email || "",
          firstName: session.user.user_metadata?.first_name || "",
          lastName: session.user.user_metadata?.last_name || "",
          role: (session.user.user_metadata?.role as UserRole) || "buyer",
          management_company_id:
            session.user.user_metadata?.management_company_id || undefined,
        };
        setUser(userData);
        localStorage.setItem("user", JSON.stringify(userData));

        // Fetch user ID and roles in parallel for better performance
        if (session.user.id) {
          const dbUserId = await getUserDatabaseId(session.user.id);
          if (dbUserId) {
            await fetchUserRoles(dbUserId);
          }
        }
      } else {
        // Check for stored user info as fallback
        const storedUser = localStorage.getItem("user");
        if (storedUser) {
          setUser(JSON.parse(storedUser));
        }
      }
      setLoading(false);
    };

    checkUser();

    // Cleanup subscription
    return () => {
      subscription.unsubscribe();
    };
  }, []);

  // Login function that handles both demo users and Supabase auth
  const login = async (
    email: string,
    password: string,
    managementCompanyId?: number
  ) => {
    try {
      // First check if this is one of our mock users
      if (email === "admin@example.com" && password === "password") {
        const mockUser = {
          id: "admin-1",
          email: "admin@example.com",
          firstName: "Admin",
          lastName: "User",
          role: "admin" as UserRole,
          management_company_id: managementCompanyId || 1, // Use selected management company ID or default
        };

        setUser(mockUser);
        localStorage.setItem("user", JSON.stringify(mockUser));

        // Set mock roles for demo admin
        const mockRoles: UserRoles = {
          platform: [{ id: 1, name: "Admin" }],
          managementCompanies: {
            [managementCompanyId || 1]: [{ id: 2, name: "Company Admin" }],
          },
          associations: {
            1: [{ id: 3, name: "Association Manager" }],
          },
        };
        setUserRoles(mockRoles);
        return;
      }

      // Check for buyer@example.com mock user
      if (email === "buyer@example.com" && password === "password") {
        const mockUser = {
          id: "buyer-1",
          email: "buyer@example.com",
          firstName: "Buyer",
          lastName: "User",
          role: "buyer" as UserRole,
          management_company_id: null, // Buyers don't have management company ID
        };

        setUser(mockUser);
        localStorage.setItem("user", JSON.stringify(mockUser));

        // Set mock roles for demo buyer
        const mockRoles: UserRoles = {
          platform: [{ id: 4, name: "Customer" }],
          managementCompanies: {},
          associations: {},
        };
        setUserRoles(mockRoles);
        return;
      }

      // For real users, use Supabase auth
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) throw error;

      // If management company ID is provided, save it in user metadata
      if (data.user && managementCompanyId) {
        await supabase.auth.updateUser({
          data: { management_company_id: managementCompanyId },
        });
      }

      // Fetch roles if we have a user
      if (data.user) {
        const dbUserId = await getUserDatabaseId(data.user.id);
        if (dbUserId) {
          await fetchUserRoles(dbUserId);
        }
      }
    } catch (error) {
      console.error("Login error:", error);
      throw error;
    }
  };

  // Register function
  const register = async (
    email: string,
    password: string,
    firstName: string,
    lastName: string
  ) => {
    setLoading(true);

    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            first_name: firstName,
            last_name: lastName,
            role: "buyer", // Default role for new users
          },
          emailRedirectTo: window.location.origin + "/login",
        },
      });

      if (error) throw error;

      // The user data will be handled by the onAuthStateChange listener if auto-confirmed
      // Otherwise they'll need to verify email
    } catch (error) {
      console.error("Registration error:", error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    try {
      await supabase.auth.signOut();
      setUser(null);
      setUserRoles(emptyRoles);
      setSelectedRole(null);
      localStorage.removeItem("user");
      localStorage.removeItem("selectedRole"); // Clear stored role on logout
    } catch (error) {
      console.error("Logout error:", error);
    }
  };

  // Update useEffect to fetch permissions on mount
  useEffect(() => {
    fetchRolePermissions();
  }, []);

  // Update selectedRole whenever it changes
  useEffect(() => {
    setCurrentSelectedRole(selectedRole);
  }, [selectedRole]);

  return (
    <AuthContext.Provider
      value={{
        user,
        currentUser: user, // Add this line to expose currentUser
        loading,
        userRoles,
        rolePermissions,
        selectedRole,
        setSelectedRole,
        fetchUserRoles,
        hasPermission,
        getCurrentRoleIds,
        login,
        register,
        logout,
        isAuthenticated: !!user,
        permissionsLoaded,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
